<template>
  <v-container fluid>
    <v-card class="mx-auto shared-csv-card">
      <v-card-title>Geteilte CSV-Transaktionen</v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <div v-if="isLoading" class="py-6 text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <TaxCsvTransactionImport
            v-if="sharedFile"
            mode="inline"
            :file="sharedFile"
            :show-file-input="false"
            auto-analyze
            @imported="handleCsvImported"
            @error="handleCsvError"
          />
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn
          variant="text"
          @click="$router.push({ name: 'Transactions' })"
        >
          Zu Transaktionen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import TaxCsvTransactionImport from '@/components/TaxCsvTransactionImport.vue'

const SHARE_DB_NAME = 'rateit-shared-images'
const SHARE_DB_VERSION = 1
const SHARE_STORE_NAME = 'incoming'

function openShareDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SHARE_DB_NAME, SHARE_DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(SHARE_STORE_NAME)) {
        db.createObjectStore(SHARE_STORE_NAME, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readonly')
    const request = transaction.objectStore(SHARE_STORE_NAME).get(shareId)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

async function deleteSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readwrite')
    transaction.objectStore(SHARE_STORE_NAME).delete(shareId)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

export default {
  name: 'SharedTransactionCsvView',

  components: {
    TaxCsvTransactionImport,
  },

  data() {
    return {
      isLoading: true,
      error: '',
      sharedRecord: null,
      sharedFile: null,
    }
  },

  async mounted() {
    await this.loadSharedCsv()
  },

  methods: {
    async loadSharedCsv() {
      this.isLoading = true
      this.error = ''

      try {
        await this.$store.dispatch('viewMe')
        await this.$store.dispatch('getStocks')
        await this.$store.dispatch('getTransactions')

        const shareId = String(this.$route.query.shareId || '').trim()
        if (!shareId) {
          this.error = 'Keine geteilte CSV gefunden.'
          return
        }

        const record = await getSharedRecord(shareId)
        const file = record?.files?.find(item => this.isCsvFile(item)) || record?.files?.[0] || null

        if (!file || !this.isCsvFile(file)) {
          this.error = 'Die geteilte Datei ist keine CSV.'
          return
        }

        this.sharedRecord = record
        this.sharedFile = file
      } catch (error) {
        console.error(error)
        this.error = 'Die geteilte CSV konnte nicht geladen werden.'
      } finally {
        this.isLoading = false
      }
    },

    isCsvFile(file) {
      const type = String(file?.type || '').toLowerCase()
      const name = String(file?.name || '').toLowerCase()
      return type === 'text/csv' ||
        type === 'application/csv' ||
        type === 'application/vnd.ms-excel' ||
        name.endsWith('.csv')
    },

    async handleCsvImported() {
      if (!this.sharedRecord?.id) return

      try {
        await deleteSharedRecord(this.sharedRecord.id)
      } catch (error) {
        console.error(error)
      }
    },

    handleCsvError(error) {
      console.error(error)
    },
  },
}
</script>

<style scoped>
.shared-csv-card {
  max-width: 960px;
}
</style>
